import { Link, Navigate } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import BookCta from "../components/BookCta";
import { PRACTICE_AREAS } from "../data/practiceAreas";
import { ARTICLES } from "../data/articles";
import { SITE } from "../data/site";

const renderRich = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );

const PracticeAreaPage = ({ slug }) => {
  const area = PRACTICE_AREAS[slug];
  if (!area) return <Navigate to="/" replace />;

  return (
    <>
      <Seo title={area.metaTitle} description={area.metaDescription} path={`/${area.slug}`} />
      <PageHero kicker={area.kicker} title={area.h1} intro={area.intro} breadcrumb={area.navName} />

      <section className="bg-white py-14 sm:py-20">
        <div className="container-site grid gap-12 lg:grid-cols-[1fr_320px]">
          <article className="legal-prose max-w-3xl" data-testid={`practice-content-${area.slug}`}>
            {area.sections.map((s) => (
              <section key={s.heading}>
                <h2>{s.heading}</h2>
                {s.blocks ? (
                  s.blocks.map((b, i) =>
                    b.type === "list" ? (
                      <ul key={i}>
                        {b.items.map((item) => (
                          <li key={item}>{renderRich(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      <p key={i}>{renderRich(b.text)}</p>
                    )
                  )
                ) : (
                  <>
                    {s.paragraphs && s.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                    {s.list && (
                      <ul>
                        {s.list.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </section>
            ))}
          </article>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div
              data-testid="practice-sidebar-cta"
              className="rounded-2xl border border-gold/30 bg-gold-soft p-6"
            >
              <p className="font-serif text-xl font-semibold text-navy">
                Hai un problema di questo tipo?
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Richiedi una valutazione della tua situazione presso lo studio a Palermo,
                in Via Salvatore Meccio 22.
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <BookCta testId="practice-book-button" className="w-full" />
                <a
                  href={SITE.phoneHref}
                  data-testid="practice-call-button"
                  className="btn-outline-navy w-full"
                >
                  <Phone size={15} aria-hidden="true" /> {SITE.phone}
                </a>
              </div>
            </div>
            {area.related && (
              <nav className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" aria-label="Aree correlate">
                <p className="text-sm font-semibold uppercase tracking-[0.15em] text-navy">
                  Potrebbe interessarti
                </p>
                <ul className="mt-3 space-y-2.5">
                  {area.related.map((r) => (
                    <li key={r}>
                      <Link
                        to={`/${r}`}
                        data-testid={`related-link-${r}`}
                        className="group flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-gold-dark"
                      >
                        <ArrowRight size={14} className="text-gold transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
                        {PRACTICE_AREAS[r].navName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
            {area.landing && (
              <Link
                to={area.landing.path}
                data-testid="practice-landing-link"
                className="group mt-6 block rounded-2xl border border-gold/40 bg-white p-5 transition-all duration-300 hover:border-gold hover:shadow-md"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark">
                  Approfondimento
                </p>
                <p className="mt-1 font-serif text-lg font-semibold leading-snug text-navy transition-colors group-hover:text-gold-dark">
                  {area.landing.label}
                </p>
                <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark">
                  Vai alla pagina <ArrowRight size={14} aria-hidden="true" />
                </span>
              </Link>
            )}
          </aside>
        </div>
        {area.articles && (
          <div className="mt-14 max-w-3xl" data-testid="practice-related-articles">
            <h2 className="font-serif text-2xl font-semibold text-navy">
              Approfondimenti su questo tema
            </h2>
            <div className="mt-5 space-y-3">
              {area.articles.map((s) => {
                const a = ARTICLES.find((x) => x.slug === s);
                if (!a) return null;
                return (
                  <Link
                    key={s}
                    to={`/approfondimenti/${s}`}
                    data-testid={`practice-article-${s}`}
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:border-gold/50 hover:shadow-md"
                  >
                    <span className="font-serif text-lg font-semibold text-navy">{a.title}</span>
                    <ArrowRight size={18} className="shrink-0 text-gold-dark transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <CtaBand />
    </>
  );
};

export default PracticeAreaPage;
